import { CSSObject, InputProps, SelectChangeEvent, styled, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { Currency } from '@/data/currencies/type';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { LocaleContext } from '@/hooks/useTranslator';
import classes from '@/styles/common.module.scss';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';

interface Props {
  readonly value: CurrencyCodeType;
  readonly currencies: readonly CurrencyCodeType[];
  readonly label?: string;

  onChange?(value: string): void;
}

export const CurrencyDropdown: React.FC<Props> = (props: Props): React.ReactElement => {
  const locale = React.useContext<Locale>(LocaleContext);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  const { value: currency, label, currencies, onChange } = props;

  const onChangeWrapper = React.useCallback(
    (event: SelectChangeEvent<unknown>): void => {
      const { value } = event.target;

      if (typeof value === 'string') {
        onChange?.(value);
      }
    },
    [onChange],
  );

  const inputProps = React.useMemo(
    (): InputProps['inputProps'] => ({ 'aria-label': `${label} currency` }),
    [label],
  );

  const currenciesWithIcons = React.useMemo(
    (): readonly Currency[] =>
      currencies.map(
        (currencyCode: CurrencyCodeType): Currency =>
          Currency.fromCode(locale, currencyCode, displayUsdcAsUsd),
      ),
    [locale, currencies, displayUsdcAsUsd],
  );

  if (currenciesWithIcons.length === 0) {
    return (
      <Box sx={styles.selectPlaceholder}>
        <Box sx={styles.animatedIcon} />
        <Box sx={styles.animatedText} />
      </Box>
    );
  }

  return (
    <CurrencySelect
      variant="outlined"
      aria-label={label}
      displayEmpty={true}
      label={label}
      value={currency}
      MenuProps={menuProps}
      inputProps={inputProps}
      onChange={onChangeWrapper}>
      {currenciesWithIcons.map(renderItem)}
    </CurrencySelect>
  );
};

const CurrencyMenuItem = styled(MenuItem)({
  height: 40,
  paddingLeft: 8,
  paddingRight: 16,
});

const CurrencySelect = styled(Select)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    backgroundColor: theme.palette.background.paper,
    lineHeight: '32px',
    height: 32,
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      paddingLeft: '8px',
      paddingRight: '35px',
      paddingTop: 16,
      lineHeight: '32px',
      width: '100%',
      height: '100%',
    },
    '& .MuiSelect-icon': {
      cursor: 'pointer',
      right: 8,
    },
  }),
);

const renderItem = (currency: Currency): React.ReactElement => {
  const IconComponent = currency.icon;

  return (
    <CurrencyMenuItem key={currency.code} value={currency.code}>
      <Box display="flex" role="button" aria-label={currency.name} alignItems="center" gap={0.5}>
        <IconComponent width={undefined} height={16} />
        <Typography
          variant="button"
          fontSize={16}
          fontWeight={400}
          lineHeight="24px"
          textTransform="none">
          {currency.name}
        </Typography>
      </Box>
    </CurrencyMenuItem>
  );
};

const styles: Record<string, SystemStyleObject> = {
  selectPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    height: 32,
    backgroundColor: 'background.paper',
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    pointerEvents: 'none',
  },
  animatedIcon: {
    width: 16,
    height: 16,
    borderRadius: 2,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
  },
  animatedText: {
    height: 16,
    width: 40,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
    borderRadius: 2,
  },
};

const menuProps = {
  classes: { paper: classes.specialPaper },
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 0,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 0,
  },
};
